import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useDispatch, useSelector } from 'react-redux';
import appActions from '../../../appActions';
import PrimeTable from '../../../components/PrimeTable';
import ProductFilterForm from './FileUploadFilterForm';
import { isPermissionCodeValid } from '../../../utils/mics';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';
import moment from 'moment';

const FileUploadList = ({ pageId }) => {

    const readAccess = isPermissionCodeValid(`${pageId}-R`);
    const writeAccess = isPermissionCodeValid(`${pageId}-W`);
    const deleteAccess = isPermissionCodeValid(`${pageId}-D`)

    let navigate = useNavigate();
    const dispatch = useDispatch();
    const fileUploadListData = useSelector((state) => state.fileUpload.fileUploadList);
    
    const fileUpload = useSelector((state) => state.fileUpload.fileupload);
    const [visible, setVisible] = useState(false);
    const [deleteRecord, setDeleteRecord] = useState(null); 

    const filters = useSelector((state) => state.fileUpload.filters);

     const getDefaultParams = (filters = {}) => ({
        ...filters,
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder: -1,
        date_from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        date_till: moment().format('YYYY-MM-DD'),
    });

    const defaultParams = useMemo(() => getDefaultParams(filters), [filters]);
    
    const defaultParamsWithoutFilters = useMemo(() => getDefaultParams(), []);

    const [formValues, setFormValues] = useState(defaultParams);

    useEffect(() => {
        if (fileUpload.id) {
            setVisible(true)
        }
    }, [fileUpload])

    const handleRefresh = useCallback(() => {
        dispatch(appActions.FILEUPLOAD_GET_ALL_REQUEST(defaultParamsWithoutFilters));
    }, [dispatch, defaultParamsWithoutFilters]);

    const handleSearch = useCallback(
        (params) => {
            const searchParams = {
                ...params,
                pagesize: params.pagesize || defaultParams?.pagesize,
                pageno: params.pageno || defaultParams?.pageno,
                sort: params.sort || defaultParams?.sort,
                sortOrder: params.sortOrder || defaultParams?.sortOrder,
            };
    
            dispatch(appActions.FILEUPLOAD_GET_ALL_REQUEST(searchParams));
        },
        [dispatch, defaultParams?.pageno, defaultParams?.pagesize, defaultParams?.sort, defaultParams?.sortOrder]  // Add these to dependencies
    );

    useEffect(() => {
        handleSearch(defaultParams);
    }, []); // Empty dependency array ensures it runs only on mount

    const handleReset = useCallback(() => {
         dispatch(appActions.FILEUPLOAD_GET_ALL_REQUEST(defaultParamsWithoutFilters));
    }, [dispatch, defaultParamsWithoutFilters]);

    const handleFormValuesChange = (values) => {
        setFormValues(values);
    };

    const handleViewRedirect = useCallback((record) => {
        const data = fileUploadListData?.data?.find(t => t.id === record.id);
        navigate('/ui/file-upload-queue/detail', { state: { data, isView: true }});
    }, [fileUploadListData, navigate]);

    const handleCloseDelete = () => {
        setVisible(false)
        dispatch(appActions.FILEUPLOAD_RESET_REQUEST(defaultParams));
        dispatch(appActions.FILEUPLOAD_GET_ALL_REQUEST(defaultParams));
    }

    const renderHeader = () => {
        return (
            <div className="d-flex flex-row gap-10 align-items-center">
                <h1 className="page-header flex">Integration Schedule Queue </h1>
                <Button icon="pi pi-sync" rounded text aria-label="Refresh" className='tp'  data-pr-tooltip="Refresh" onClick={handleRefresh} />
            </div>
        );
    };

   const handleDelete = useCallback((record) => {
        setDeleteRecord(record); 
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (deleteRecord) {
            dispatch(appActions.FILEUPLOAD_DELETE_REQUEST({ id: deleteRecord.id }));
            setDeleteRecord(null); // Clear the record after deletion
    
            // Explicitly refresh the table after deletion
            setTimeout(() => {
                handleRefresh();
            }, 500); // Add a slight delay if needed to allow the backend to process
        }
    }, [dispatch, deleteRecord, handleRefresh])

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                { readAccess && 
                    <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                        <i className="pi pi-eye text-blue-500 text-base"></i>
                    </span>
                }
                { deleteAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                        <i className="pi pi-times-circle text-base text-danger" />
                    </span>
                }
            </div>
        ),
        [handleViewRedirect, readAccess, handleDelete, deleteAccess],
    );

    const onFilterChangeCallback  = useCallback(
        (e) => {

            const { sortField, sortOrder, rows, page } = e;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); // Call search with updated sorting params
        },
        [handleSearch, formValues],
    );
    
    
    const columns = [
        { field: 'fileId', header: 'File Id', sortable: true , isLink : true, linkUrl : "/ui/integration-file" },
        { field: 'storeId', header: 'Store Id', sortable: true },
        { field: 'uploadGroup', header: 'Upload Group', sortable: true },
        { field: 'uploadScheduleOn', header: 'Upload Schedule On', sortable: true, columnType : 'datetime'  },
        { field: 'uploadStatus', header: 'Upload Status', sortable: true },
        { field: 'uploadMethod', header: 'Upload Method', sortable: true },
        { field: 'uploadTime', header: 'Upload Time', sortable: true },
        { field: 'error', header: 'Error', sortable: true },
        { field: 'processed', header: 'Processed', sortable: true },
        { field: 'status', header: 'Status', sortable: true },
        { field: 'createdOn', header: 'Created On', sortable: true,  width: '140px', columnType : 'datetime'  },
        // { field: 'lastUpdated', header: 'Last Updated', sortable: true, columnType : 'datetime'  },
        // { field: 'lastUpdatedBy', header: 'Last Updated By', sortable: true },
    ];
    const renderList = () => {
        return (
            <>
                <div className="grid px-4 align-items-center my-2">
                    {renderHeader()}
                </div>
                <div className="bg-white mx-4 pv-20 ph-20 br-6 shadow-white">
                    <ProductFilterForm handleSearch={handleSearch} handleReset={handleReset}  onFormValuesChange={handleFormValuesChange}  />
                    <div className='custom-devider'></div>
                    <PrimeTable
                        list={fileUploadListData}
                        columns={columns}
                        actionColumn={actionTemplate}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        tableName='integration_schedule_queue'
                        isLazy={true}
                        showGenerateXmlBtn={false}
                        pageId={ pageId }
                    />
                </div>
                <Dialog
                    className="bg-white pt-4"
                    showHeader={false}
                    header="Header"
                    visible={visible}
                    onHide={() => setVisible(false)}
                    style={{ textAlign: 'center' }}
                >
                    <p>File upload deleted successfully</p>
                    <Button
                        type="button"
                        label="Ok"
                        className="mt-2 p-button p-component p-button-success mr-2 mh-4"
                        onClick={() => handleCloseDelete()}
                    />
                </Dialog>
            </>
        );
    };

    return <div className="form_height">
            {renderList()}
            {deleteRecord && (
                <ConfirmationDialog
                    message={`Are you sure you want to delete the Integration Schedule Queue : ${deleteRecord.id}?`}
                    onAccept={handleDeleteConfirm}
                    onReject={() => setDeleteRecord(null)}
                />
            )}
    </div>;
};

export default FileUploadList;
